import React, { useState } from "react";
import { CCField } from "@pymblecitizencoders/cc-ui";
import GooglePlacesAutocomplete, {
	geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faArrowUpRightFromSquare,
	faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { EventDoc } from "../../types/event";
import { app } from "../../config";
import "./eventManager.css";

interface EventAddressTabProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * Static Maps zoom for the preview.
 *
 * 13 rather than a street-level 15: the point of a 300px-tall strip is to show
 * roughly where in Sydney the event is, and at 15 the picture is close enough
 * that the surrounding suburbs are cropped out and nothing places it. Higher
 * numbers are closer in — 15 was the previous value.
 */
const MAP_ZOOM = 13;

/**
 * A usable coordinate, or null.
 *
 * Guarding on "not undefined and not empty" is not enough: the API sends `null`
 * for an event that has no location, and `null` passes both of those. It then
 * stringifies to the literal "null", which is how a map URL ended up asking
 * Google for `query=null,null`. Anything that is not a finite number — null,
 * blank, or junk in a legacy document — is treated as "no
 * coordinate" and the map falls back to the address or the campus. Stored
 * values still arrive in every shape, which is why the guard stays.
 */
const coordinate = (
	value: number | string | null | undefined,
): string | null => {
	if (value === null || value === undefined) return null;
	const text = String(value).trim();
	if (!text) return null;
	return Number.isFinite(Number(text)) ? text : null;
};

/** What the autocomplete hands back on selection. */
interface PlaceSelection {
	value: {
		place_id: string;
		description: string;
		structured_formatting?: { main_text?: string };
	};
}

/**
 * Address tab — pymble-swipe's AddressTab concept (address entry above a map).
 *
 * Search is v2's: Google Places autocomplete, then `geocodeByPlaceId` to turn
 * the chosen place into coordinates, writing Name, Address, Lat and Lon in one
 * go. Picking a place by hand and then typing its latitude is not a thing
 * anyone should have to do.
 *
 * The map is a Static Maps picture, as v2's `StaticGoogleMap` is — see `mapSrc`
 * for why the live embed had to go — and it opens on the campus until the event
 * has an address of its own.
 *
 * One deliberate difference from v2:
 *
 *   - There is ONE address field, and it is the search. v2 hides the address
 *     behind a "Change" button once set; an earlier version here had a search
 *     box above a separate Address input, which meant two places showing one
 *     fact and a search that emptied itself after every choice. Typing searches,
 *     picking fills the coordinates, and text the search cannot match is kept
 *     as typed.
 *
 *   - Latitude and longitude have no inputs. They are written by the search and
 *     read by the map; hand-editing them only ever produced a pin that
 *     disagreed with the address above it.
 */
export const EventAddressTab: React.FC<EventAddressTabProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	const location = event.Location ?? {};
	const [lookupError, setLookupError] = useState("");

	const lat = coordinate(location.Lat);
	const lon = coordinate(location.Lon);
	const hasCoords = lat !== null && lon !== null;

	const patchLocation = (patch: Partial<typeof location>) =>
		onChange({ Location: { ...location, ...patch } });

	const onPlaceSelected = async (place: PlaceSelection | null) => {
		if (!place?.value?.place_id) return;
		try {
			const [result] = await geocodeByPlaceId(place.value.place_id);
			const point = result?.geometry?.location;
			if (!point) throw new Error("no geometry");
			patchLocation({
				Name: place.value.structured_formatting?.main_text ?? location.Name,
				Address: place.value.description,
				Lat: String(point.lat()),
				Lon: String(point.lng()),
			});
			setLookupError("");
		} catch {
			// No detail surfaced or logged — the geocoder echoes the query back,
			// and the fields below are always available as the manual route.
			setLookupError("Could not look that address up. Enter it below instead.");
		}
	};

	// Address is trimmed too: a location saved as whitespace should give the
	// campus view, not a Google search for a space.
	const mapQuery = hasCoords
		? `${lat},${lon}`
		: (location.Address?.trim() ?? "");

	/**
	 * With nothing entered the map opens on the campus rather than on a
	 * placeholder. Most events are held at the school, so it is usually already
	 * showing the right place, and an empty grey box tells nobody anything.
	 *
	 * No marker in that state, deliberately: a pin would read as "the location
	 * is set to the school" when it is not. The pin appears once there is a real
	 * address to put it on.
	 */
	const isDefaultView = !mapQuery;
	const mapCentre = isDefaultView
		? `${app.defaultLocation.lat},${app.defaultLocation.lng}`
		: mapQuery;

	/**
	 * Static Maps, as v2 uses — NOT the Maps Embed API this tab used to call.
	 *
	 * The Embed API is not activated on the project: the iframe rendered a black
	 * panel reading "Google Maps Platform rejected your request. This API is not
	 * activated on your API project", and a direct request returns 403. Static
	 * Maps answers 200 on the same key, which is why v2's map works and this one
	 * did not. The trade is a picture instead of a pannable map; a working
	 * picture beats a broken map, and enabling the Embed API would let this
	 * switch back.
	 *
	 * No react-static-google-map dependency: the package renders exactly this
	 * URL into an <img>, and v2 already carries four Google packages it never
	 * uses.
	 */
	const mapSrc = app.googleMapsApiKey
		? "https://maps.googleapis.com/maps/api/staticmap?" +
			new URLSearchParams({
				center: mapCentre,
				zoom: String(MAP_ZOOM),
				/* 640 is the Static Maps ceiling for a width — asking for more
				   is silently clamped. The 3.2:1 shape suits the band this sits
				   in, so the picture is not cropped to fit (see .em-map img). */
				size: "640x200",
				// Retina, so the picture is not soft on the screens this is used on.
				scale: "2",
				// See isDefaultView: no pin until there is an address to pin.
				...(isDefaultView
					? {}
					: { markers: `color:0x002f67|label:P|${mapCentre}` }),
				key: app.googleMapsApiKey,
			}).toString()
		: null;

	/**
	 * The picture is not pannable, so clicking it hands the location to the real
	 * Google Maps — which is where anyone wanting to zoom, get directions or look
	 * at Street View was going to end up anyway.
	 *
	 * The `search/?api=1` form is Google's documented, stable URL scheme and takes
	 * either coordinates or a plain address, which is exactly what `mapCentre`
	 * already holds for both states.
	 */
	const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
		mapCentre,
	)}`;
	const mapsLabel = `Open ${
		isDefaultView
			? app.defaultLocation.label
			: location.Address?.trim() || mapCentre
	} in Google Maps (opens in a new tab)`;

	return (
		<div>
			<h6 className="em-section-heading">Set address</h6>

			{app.googleMapsApiKey && !readOnly ? (
				<div className="em-address-search">
					<label className="em-label" htmlFor="em-address-lookup">
						Address
					</label>
					<div className="em-address-search__field">
						<FontAwesomeIcon
							icon={faMagnifyingGlass}
							className="em-address-search__icon"
							aria-hidden="true"
						/>
						{/* One field, not a search above a text box.
						    
						    The two held the same fact, and the search cleared itself
						    after every choice — so the box you had just typed into went
						    empty while the answer appeared somewhere else. This shows
						    the stored address, searches as you type, and fills the
						    coordinates from whatever you pick.
						    
						    Free text still works: `onInputChange` writes every keystroke
						    to the draft, so an address Google cannot find is typed in
						    and kept. It clears the coordinates as it goes — they came
						    from a place that is no longer what the field says, and a map
						    pinned to the old one is worse than no pin. */}
						<GooglePlacesAutocomplete
							apiKey={app.googleMapsApiKey}
							selectProps={{
								inputId: "em-address-lookup",
								classNamePrefix: "em-places",
								placeholder: "Start typing an address…",
								value: null,
								inputValue: location.Address ?? "",
								controlShouldRenderValue: false,
								// The menu is the only thing worth keeping open; a
								// cleared box on blur would throw away typed text.
								blurInputOnSelect: true,
								onInputChange: (text: string, meta: { action: string }) => {
									if (meta.action !== "input-change") return;
									patchLocation({ Address: text, Lat: null, Lon: null });
								},
								onChange: (place: unknown) =>
									void onPlaceSelected(place as PlaceSelection),
								"aria-label": "Address",
							}}
						/>
					</div>
					{lookupError ? (
						<span className="em-error" role="alert">
							{lookupError}
						</span>
					) : (
						<span className="em-muted">
							Choose a result to place it on the map, or type an address the
							search cannot find.
						</span>
					)}
				</div>
			) : (
				/* No key, or read-only: a plain field, since the autocomplete has
				   nothing to call. */
				<CCField
					label="Address"
					type="text"
					fullWidth
					value={location.Address ?? ""}
					disabled={readOnly}
					onChange={(e) => patchLocation({ Address: e.target.value })}
				/>
			)}

			<div className="em-map">
				{mapSrc ? (
					/* noreferrer as well as noopener: the URL carries the event's
					   address, and there is no reason to tell Google which page
					   it came from on top of that. */
					<a
						className="em-map__link"
						href={mapsUrl}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={mapsLabel}
					>
						<img
							/* Empty alt because the link is what carries the
							   accessible name — an alt here would have a screen
							   reader announce the same place twice. */
							alt=""
							src={mapSrc}
							loading="lazy"
						/>
						<span className="em-map__badge" aria-hidden="true">
							<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
							Google Maps
						</span>
					</a>
				) : (
					<div className="em-map-empty">
						Set VITE_GOOGLE_MAPS_API_KEY to preview the map.
					</div>
				)}
			</div>

			{mapSrc && isDefaultView && (
				<span className="em-muted em-map-caption">
					Showing {app.defaultLocation.label}. Enter an address or coordinates
					to move the map.
				</span>
			)}
		</div>
	);
};
